//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  PACKAGE_INSERT_CLIENT_PROVIDED,
  PACKAGE_INSERT_NONE,
  PackageInsertType,
  SamplingCampaignRequest,
  SamplingCampaignRequestCountry,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import {
  PurchaseOption,
  SamplingContentType,
  SamplingDeliveryMethod,
  SamplingFulfillmentOption,
  SamplingOpportunityInsertType,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import {
  FULFILLMENT_TYPE_DEALS,
  FULFILLMENT_TYPE_SHIPPED,
  WHO_SHIPS_PRODUCT_BAZAARVOICE,
  WHO_SHIPS_PRODUCT_MY_COMPANY,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { isPurchaseOptionExpired } from '@campaigns/utils/sampling/SamplingCampaignRequestValidationRulesUtils';
import { isDefined, isUndefined } from '@utils/filterPredicates';

import { RoutableStateName } from 'app/top-level-states';

export const calculateRemainingUnits = (
  formValues: Pick<SamplingCampaignRequest, 'purchaseOption' | 'products'>,
): number => {
  const purchaseOptions = formValues.purchaseOption ?? [];
  const availableUnits = purchaseOptions.reduce(
    (sum, option) => sum + (option.balance ?? 0),
    0,
  );
  const products = formValues.products ?? [];
  const allocatedUnits = products.reduce(
    (sum, product) => sum + (product.quantity ?? 0),
    0,
  );
  return availableUnits - allocatedUnits;
};

const isLocaleSupported = (
  fulfillmentOptions: SamplingFulfillmentOption[],
  locale?: string,
): boolean => {
  if (!locale) {
    return true;
  }

  const localeParts = locale.split('_');
  if (localeParts.length < 2) {
    return false;
  }

  const requestCountry = localeParts[1] as SamplingCampaignRequestCountry;
  const allAvailableCountryCodes = new Set(
    fulfillmentOptions.flatMap((option) => option.availableCountryCodes ?? []),
  );

  return allAvailableCountryCodes.has(requestCountry);
};

const isParticipantCountSupported = (
  fulfillmentOptions: SamplingFulfillmentOption[],
  participantCount?: number,
): boolean => {
  if (!isDefined(participantCount)) {
    return true;
  }

  const totalCartonCount = fulfillmentOptions.reduce(
    (sum, option) => sum + (option.cartonCount ?? 0),
    0,
  );

  return totalCartonCount >= participantCount;
};

const packageInsertToFulfillmentOptionMap: Record<
  PackageInsertType,
  SamplingOpportunityInsertType
> = {
  [PACKAGE_INSERT_CLIENT_PROVIDED]:
    SamplingOpportunityInsertType.CLIENT_SUPPLIED,
  [PACKAGE_INSERT_NONE]: SamplingOpportunityInsertType.NO,
};

const isPackageInsertSupported = (
  fulfillmentOptions: SamplingFulfillmentOption[],
  packageInsert?: PackageInsertType,
): boolean => {
  if (isUndefined(packageInsert)) {
    return true;
  }

  const supportedInserts = new Set(
    fulfillmentOptions.map((option) => option.insert).filter(isDefined),
  );

  if (supportedInserts.size === 0) {
    return false;
  }

  const expectedInsert = packageInsertToFulfillmentOptionMap[packageInsert];

  return supportedInserts.has(expectedInsert);
};

const hasFulfillmentOptionsMismatch = (
  formValues: SamplingCampaignRequest,
  purchaseOption: PurchaseOption,
): boolean => {
  const fulfillmentOptions = purchaseOption.fulfillmentOptions ?? [];

  if (fulfillmentOptions.length === 0) {
    return true;
  }

  const supportsLocale = isLocaleSupported(
    fulfillmentOptions,
    formValues.locale,
  );
  const supportsParticipantCount = isParticipantCountSupported(
    fulfillmentOptions,
    formValues.fulfillmentOptions?.participantCount,
  );
  const supportsPackageInsert = isPackageInsertSupported(
    fulfillmentOptions,
    formValues.fulfillmentOptions?.packageInsert,
  );

  return !supportsLocale || !supportsParticipantCount || !supportsPackageInsert;
};

const hasContentTypeMismatch = (
  formValues: SamplingCampaignRequest,
  purchaseOption: PurchaseOption,
): boolean => {
  const tasks = formValues.memberTasks ?? {};
  const contentType = purchaseOption.contentType;
  if (contentType === SamplingContentType.REVIEWS_ONLY && tasks.postOnSocial) {
    return true;
  }
  if (contentType === SamplingContentType.SOCIAL_ONLY && tasks.writeReview) {
    return true;
  }
  return false;
};

const hasDeliveryMethodMismatch = (
  formValues: SamplingCampaignRequest,
  purchaseOption: PurchaseOption,
): boolean => {
  const { fulfillmentType, whoShipsProduct } =
    formValues.fulfillmentOptions || {};
  const deliveryMethod = purchaseOption.deliveryMethod;

  if (deliveryMethod === SamplingDeliveryMethod.DigitalDelivery) {
    if (fulfillmentType !== FULFILLMENT_TYPE_DEALS) {
      return true;
    }
  } else if (deliveryMethod === SamplingDeliveryMethod.Shipped) {
    if (
      fulfillmentType !== FULFILLMENT_TYPE_SHIPPED ||
      whoShipsProduct !== WHO_SHIPS_PRODUCT_MY_COMPANY
    ) {
      return true;
    }
  } else if (deliveryMethod === SamplingDeliveryMethod.ShippedByBazaarvoice) {
    if (
      fulfillmentType !== FULFILLMENT_TYPE_SHIPPED ||
      whoShipsProduct !== WHO_SHIPS_PRODUCT_BAZAARVOICE
    ) {
      return true;
    }
  } else if (isUndefined(deliveryMethod)) {
    return false;
  }

  return false;
};

export const shouldTriggerUnitsModal = (
  targetState: RoutableStateName,
  formValues: SamplingCampaignRequest,
  isSamplingPrepaidCampaignsEnabled: boolean,
  currentState: RoutableStateName,
): boolean => {
  if (!isSamplingPrepaidCampaignsEnabled) {
    return false;
  }

  if (formValues.isNewPurchase) {
    return false;
  }

  const isPrepaidProducts =
    currentState ===
    'campaigns.sampling-prepaid-campaign-request-details-edit-products';

  if (!isPrepaidProducts) {
    return false;
  }

  const isNavigatingAway =
    targetState !==
    'campaigns.sampling-prepaid-campaign-request-details-edit-products';

  if (!isNavigatingAway) {
    return false;
  }

  return true;
};

export const shouldTriggerLeaveUnitsBehindModal = (
  targetState: RoutableStateName,
  formValues: SamplingCampaignRequest,
  isSamplingPrepaidCampaignsEnabled: boolean,
  currentState: RoutableStateName,
): boolean => {
  if (
    !shouldTriggerUnitsModal(
      targetState,
      formValues,
      isSamplingPrepaidCampaignsEnabled,
      currentState,
    )
  ) {
    return false;
  }

  const purchaseOptions = formValues.purchaseOption ?? [];

  if (purchaseOptions.length === 0) {
    return false;
  }

  const isSingleUse = purchaseOptions[0]?.areUnitsFlexible === false;

  if (!isSingleUse) {
    return false;
  }

  const remainingUnits = calculateRemainingUnits(formValues);
  return remainingUnits > 0;
};

export const shouldTriggerNeedMoreUnitsModal = (
  targetState: RoutableStateName,
  formValues: SamplingCampaignRequest,
  isSamplingPrepaidCampaignsEnabled: boolean,
  currentState: RoutableStateName,
): boolean => {
  if (
    !shouldTriggerUnitsModal(
      targetState,
      formValues,
      isSamplingPrepaidCampaignsEnabled,
      currentState,
    )
  ) {
    return false;
  }

  const remainingUnits = calculateRemainingUnits(formValues);
  return remainingUnits < 0;
};

export const shouldTriggerCreateNewPurchaseConfirmationModal = (
  formValues: SamplingCampaignRequest,
  isSamplingPrepaidCampaignsEnabled: boolean,
): boolean => {
  if (!isSamplingPrepaidCampaignsEnabled) {
    return false;
  }

  if (formValues.isNewPurchase) {
    return false;
  }

  const purchaseOption = formValues.purchaseOption?.[0];
  if (!purchaseOption) {
    return false;
  }

  if (calculateRemainingUnits(formValues) < 0) {
    return true;
  }

  if (hasFulfillmentOptionsMismatch(formValues, purchaseOption)) {
    return true;
  }

  if (hasContentTypeMismatch(formValues, purchaseOption)) {
    return true;
  }

  if (isPurchaseOptionExpired(purchaseOption)) {
    return true;
  }

  if (hasDeliveryMethodMismatch(formValues, purchaseOption)) {
    return true;
  }

  return false;
};
